import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { Box, styled, IconButton, useTheme, Tooltip } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

import Typography from '../components/atoms/Typography';
import useImperativeIntl from '../hooks/useImperativeIntl';

const Legend = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(1.25, 1, 0, 1),
  width: '100%'
}));

const ItemsContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  overflowX: 'hidden',
  gap: theme.spacing(2),
  // Accessibility tweak to display focus indicator properly
  padding: theme.spacing(0.5),
  margin: theme.spacing(-0.5)
}));

const Item = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(1),
  alignItems: 'center',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  cursor: 'pointer'
}));

const OverflowVeil = styled(Box)(({ theme }) => ({
  display: 'inline-block',
  position: 'absolute',
  left: theme.spacing(-2),
  top: theme.spacing(1),
  bottom: theme.spacing(1),
  zIndex: 1,
  width: theme.spacing(2.5),
  background: `linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, ${theme.palette.background.paper} 100%)`
}));

const ShowMoreButtons = styled(Box)(({ theme }) => ({
  position: 'absolute',
  padding: theme.spacing(0.25, 1),
  top: theme.spacing(0),
  right: 0,
  background: theme.palette.background.paper
}));

const Circle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'color'
})(({ theme, color }) => {
  const size = theme.spacing(1);
  return {
    backgroundColor: color,
    borderRadius: '50%',
    width: size,
    height: size
  };
});

export default function ChartLegend({ series, selectedCategories, onCategoryClick }) {
  const theme = useTheme();
  const [overflowing, setOverflowing] = useState(false);
  const [offset, setOffset] = useState(0);
  const [maxWidth, setMaxWidth] = useState(undefined);
  const legendRef = useRef(null);
  const containerRef = useRef(null);
  const showMoreButtonsRef = useRef(null);

  const intl = useIntl();
  const intlConfig = useImperativeIntl(intl);

  const handleClickRight = () => {
    setOffset(offset + 1);
  };

  const handleClickLeft = () => {
    setOffset(Math.max(offset - 1));
  };

  const onCategoryPress = (e, categoryName) => {
    if (e.key === 'Enter') {
      onCategoryClick(categoryName);
    }
  };

  const updateMaxWidth = useCallback(() => {
    const overflowing =
      containerRef.current &&
      containerRef.current.scrollWidth > containerRef.current.clientWidth;

    setOverflowing(overflowing);
    setMaxWidth(
      overflowing && containerRef.current
        ? legendRef.current.clientWidth - (showMoreButtonsRef.current?.clientWidth || 70)
        : legendRef.current?.clientWidth - 12 || 500
    );
  }, [legendRef, containerRef, showMoreButtonsRef]);

  useEffect(() => {
    updateMaxWidth();
  }, [
    containerRef,
    containerRef.current?.scrollWidth,
    containerRef.current?.clientWidth,
    updateMaxWidth,
    offset
  ]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    let observer;
    if (legendRef.current) {
      observer = new ResizeObserver(() => {
        updateMaxWidth();
      });
      observer.observe(legendRef.current);
    }
    return () => {
      observer?.disconnect();
    };
  }, [legendRef, updateMaxWidth]);

  return (
    <Legend ref={legendRef}>
      <ItemsContainer
        ref={containerRef}
        style={{
          maxWidth: `${maxWidth}px`
        }}
      >
        {series.map((category, i) => {
          if (i < offset) return null;

          const selected =
            selectedCategories.length === 0 || selectedCategories.includes(category.name);

          return (
            <Item
              key={i}
              tabIndex={0}
              onClick={onCategoryClick ? () => onCategoryClick(category.name) : undefined}
              onKeyDown={
                onCategoryClick ? (e) => onCategoryPress(e, category.name) : undefined
              }
              style={{
                pointerEvents:
                  !onCategoryClick || category.name === 'Others' ? 'none' : undefined
              }}
            >
              <Circle color={selected ? category.color : theme.palette.text.disabled} />
              <Typography
                variant='overline'
                color={selected ? undefined : 'text.disabled'}
              >
                {category.name}
              </Typography>
            </Item>
          );
        })}
      </ItemsContainer>
      {(overflowing || offset > 0) && (
        <ShowMoreButtons ref={showMoreButtonsRef}>
          <OverflowVeil />
          <Tooltip
            title={intlConfig.formatMessage({ id: 'c4r.widgets.chartLegend.next' })}
          >
            <IconButton size='small' disabled={offset === 0} onClick={handleClickLeft}>
              <ChevronLeft />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={intlConfig.formatMessage({ id: 'c4r.widgets.chartLegend.prev' })}
          >
            <IconButton size='small' disabled={!overflowing} onClick={handleClickRight}>
              <ChevronRight />
            </IconButton>
          </Tooltip>
        </ShowMoreButtons>
      )}
    </Legend>
  );
}
